/**
 * Environment detection utility
 * Automatically detects the current environment based on various factors
 */

export type Environment = 'development' | 'production' | 'test';

/**
 * Detects the current environment
 * Priority: NODE_ENV -> import.meta.env.MODE -> hostname -> default to production
 */
export function getCurrentEnvironment(): Environment {
  // 1. Check NODE_ENV (most reliable for build processes)
  if (typeof process !== 'undefined' && process.env?.NODE_ENV) {
    const nodeEnv = process.env.NODE_ENV.toLowerCase();
    if (nodeEnv === 'development' || nodeEnv === 'test') {
      return nodeEnv as Environment;
    }
    if (nodeEnv === 'production') {
      return 'production';
    }
  }

  // 2. Check Vite's import.meta.env.MODE
  if (typeof import.meta !== 'undefined' && import.meta.env?.MODE) {
    const mode = import.meta.env.MODE.toLowerCase();
    if (mode === 'development' || mode === 'test') {
      return mode as Environment;
    }
    if (mode === 'production') {
      return 'production';
    }
  }

  // 3. Check hostname (for runtime detection in browser)
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('192.168.')) {
      return 'development';
    }
  }

  // 4. Default to production for safety
  return 'production';
}

/**
 * Checks if we're in development mode
 */
export function isDevelopment(): boolean {
  return getCurrentEnvironment() === 'development';
}

/**
 * Checks if we're in production mode
 */
export function isProduction(): boolean {
  return getCurrentEnvironment() === 'production';
}

/**
 * Checks if we're in test mode
 */
export function isTest(): boolean {
  return getCurrentEnvironment() === 'test';
}

/**
 * Gets environment-specific configuration
 */
export function getEnvironmentConfig() {
  const env = getCurrentEnvironment();
  
  return {
    environment: env,
    isDev: env === 'development',
    isProd: env === 'production',
    isTest: env === 'test',
    firebase: {
      projectId: import.meta.env.VITE_REACT_FIREBASE_PROJECT_ID,
      // Add environment suffix for non-production environments
      projectSuffix: env === 'production' ? '' : `-${env}`
    }
  };
}